import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import { sha256Hex, type FileStore } from "../fileStore";
import {
  managedRelativePath,
  mimeFromFilename,
  type Attachment,
  type AttachmentKind,
} from "../../domain/attachment";
import { ValidationError } from "./projectRepository";

interface AttachmentRow {
  id: string;
  kind: string;
  original_filename: string;
  stored_relative_path: string;
  mime_type: string;
  size_bytes: number;
  sha256: string;
  imported_at: string;
  archived_at: string | null;
}

function mapAttachment(r: AttachmentRow): Attachment {
  return {
    id: r.id,
    kind: r.kind as AttachmentKind,
    originalFilename: r.original_filename,
    storedRelativePath: r.stored_relative_path,
    mimeType: r.mime_type,
    sizeBytes: Number(r.size_bytes),
    sha256: r.sha256,
    importedAt: r.imported_at,
    archivedAt: r.archived_at,
  };
}

/** Which financial record an attachment is linked to. Each target has its
 *  own association table with a real foreign key (no polymorphism). */
export type AttachmentTarget =
  | { type: "purchase"; id: string }
  | { type: "expense"; id: string }
  | { type: "product"; id: string }
  /** Phase 13: a generated report the user chose to keep with the project. */
  | { type: "project"; id: string }
  /** Spec §68U: a contractor's returned proposal, attached to their quote. */
  | { type: "quote"; id: string }
  /** Spec §73E: the signed loan agreement, attached to its financing facility. */
  | { type: "financing"; id: string };

/** Association table per target type — each with a real foreign key. */
const LINK_TABLES: Record<AttachmentTarget["type"], { table: string; column: string }> = {
  purchase: { table: "purchase_attachments", column: "purchase_id" },
  expense: { table: "actual_expense_attachments", column: "expense_id" },
  product: { table: "product_attachments", column: "product_id" },
  project: { table: "project_attachments", column: "project_id" },
  quote: { table: "contractor_quote_attachments", column: "quote_id" },
  financing: { table: "financing_facility_attachments", column: "facility_id" },
};

export interface ImportResult {
  attachment: Attachment;
  /** True when the bytes already existed and the existing record was reused. */
  duplicateOfExisting: boolean;
}

export class AttachmentRepository {
  constructor(
    private readonly db: DbExecutor,
    private readonly files: FileStore,
  ) {}

  async get(id: string): Promise<Attachment | null> {
    const rows = await this.db.select<AttachmentRow>("SELECT * FROM attachments WHERE id = ?", [id]);
    return rows.length > 0 ? mapAttachment(rows[0]) : null;
  }

  async listAll(): Promise<Attachment[]> {
    const rows = await this.db.select<AttachmentRow>(
      "SELECT * FROM attachments ORDER BY imported_at DESC",
    );
    return rows.map(mapAttachment);
  }

  /** Find an existing (non-archived) attachment with identical bytes. */
  async findBySha256(sha256: string): Promise<Attachment | null> {
    const rows = await this.db.select<AttachmentRow>(
      "SELECT * FROM attachments WHERE sha256 = ? AND archived_at IS NULL ORDER BY imported_at LIMIT 1",
      [sha256],
    );
    return rows.length > 0 ? mapAttachment(rows[0]) : null;
  }

  /**
   * Import bytes into managed storage. If identical bytes already exist,
   * the existing attachment is returned (`duplicateOfExisting: true`) and
   * NO second copy is written — callers decide whether to link it.
   */
  async importBytes(
    bytes: Uint8Array,
    originalFilename: string,
    kind: AttachmentKind,
    options: { allowDuplicate?: boolean } = {},
  ): Promise<ImportResult> {
    if (originalFilename.trim() === "") throw new ValidationError(["A filename is required."]);
    if (bytes.byteLength === 0) throw new ValidationError(["The file is empty."]);
    const hash = await sha256Hex(bytes);
    if (!options.allowDuplicate) {
      const existing = await this.findBySha256(hash);
      if (existing) return { attachment: existing, duplicateOfExisting: true };
    }
    const now = new Date();
    const id = generateId();
    const relativePath = managedRelativePath(kind, id, originalFilename, now);
    await this.files.writeFile(relativePath, bytes);
    const attachment: Attachment = {
      id,
      kind,
      originalFilename: originalFilename.trim(),
      storedRelativePath: relativePath,
      mimeType: mimeFromFilename(originalFilename),
      sizeBytes: bytes.byteLength,
      sha256: hash,
      importedAt: now.toISOString(),
      archivedAt: null,
    };
    await this.db.execute(
      `INSERT INTO attachments (id, kind, original_filename, stored_relative_path, mime_type,
         size_bytes, sha256, imported_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        attachment.id, attachment.kind, attachment.originalFilename, attachment.storedRelativePath,
        attachment.mimeType, attachment.sizeBytes, attachment.sha256, attachment.importedAt,
      ],
    );
    return { attachment, duplicateOfExisting: false };
  }

  /* ---------- Links (one association table per target type) ---------- */

  async link(attachmentId: string, target: AttachmentTarget): Promise<void> {
    const { table, column } = LINK_TABLES[target.type];
    await this.db.execute(
      `INSERT OR IGNORE INTO ${table} (${column}, attachment_id) VALUES (?, ?)`,
      [target.id, attachmentId],
    );
  }

  async unlink(attachmentId: string, target: AttachmentTarget): Promise<void> {
    const { table, column } = LINK_TABLES[target.type];
    await this.db.execute(`DELETE FROM ${table} WHERE ${column} = ? AND attachment_id = ?`, [
      target.id,
      attachmentId,
    ]);
  }

  async listFor(target: AttachmentTarget): Promise<Attachment[]> {
    const { table, column } = LINK_TABLES[target.type];
    const rows = await this.db.select<AttachmentRow>(
      `SELECT a.* FROM attachments a JOIN ${table} l ON l.attachment_id = a.id
        WHERE l.${column} = ? ORDER BY a.imported_at`,
      [target.id],
    );
    return rows.map(mapAttachment);
  }

  /** How many records reference this attachment (all association tables). */
  async linkCount(attachmentId: string): Promise<number> {
    const rows = await this.db.select<{ c: number }>(
      `SELECT (SELECT COUNT(*) FROM purchase_attachments WHERE attachment_id = ?) +
              (SELECT COUNT(*) FROM actual_expense_attachments WHERE attachment_id = ?) +
              (SELECT COUNT(*) FROM product_attachments WHERE attachment_id = ?) +
              (SELECT COUNT(*) FROM project_attachments WHERE attachment_id = ?) +
              (SELECT COUNT(*) FROM contractor_quote_attachments WHERE attachment_id = ?) +
              (SELECT COUNT(*) FROM financing_facility_attachments WHERE attachment_id = ?) +
              (SELECT COUNT(*) FROM financing_extractions WHERE attachment_id = ?) +
              (SELECT COUNT(*) FROM document_extractions WHERE attachment_id = ?) +
              (SELECT COUNT(*) FROM draw_request_attachments WHERE attachment_id = ?) + (SELECT COUNT(*) FROM worksheet_issue_attachments99 WHERE attachment_id = ?) AS c`,
      Array(10).fill(attachmentId),
    );
    return Number(rows[0].c);
  }

  /* ---------- File presence / archive ---------- */

  /** True when the managed file is present on disk. */
  async fileExists(attachment: Attachment): Promise<boolean> {
    return this.files.exists(attachment.storedRelativePath);
  }

  async readBytes(attachment: Attachment): Promise<Uint8Array> {
    return this.files.readFile(attachment.storedRelativePath);
  }

  async absolutePath(attachment: Attachment): Promise<string> {
    return this.files.absolutePath(attachment.storedRelativePath);
  }

  /** Archive the record (never physically deletes evidence). */
  async archive(attachmentId: string): Promise<void> {
    await this.db.execute(
      "UPDATE attachments SET archived_at = ? WHERE id = ? AND archived_at IS NULL",
      [new Date().toISOString(), attachmentId],
    );
  }
}
